import { useTranslation } from 'react-i18next';

import { PharmacyCard, type Pricing } from '@phx/myphx-lib';

import { useMedicationConfigOverride } from '../../../../../contexts/DrugProgramProvider';
import type {
    AuthorizedMedicationInfoFragment,
    MailOrderProviderInfoFragment,
    PharmacyProviderInfoFragment,
    UnknownProviderInfoFragment,
} from '../../../../../graphql/generated/graphql';
import {
    formatToLocalizedCurrency,
    getProviderName,
} from '../../../../../util';
import { getPricing } from '../../../../../util/config/get-pricing';

export const ProviderOffersCard = ({
    provider,
    medication,
    onSelected,
}: {
    provider:
        | MailOrderProviderInfoFragment
        | PharmacyProviderInfoFragment
        | UnknownProviderInfoFragment;
    medication: AuthorizedMedicationInfoFragment;
    onSelected: (id: string, providerId?: string) => void;
}) => {
    const { t } = useTranslation();
    const configOverride = useMedicationConfigOverride();
    const { pharmacyOverrides } = configOverride;
    const medicationPricing = getPricing(configOverride, medication);
    const shippingTime = pharmacyOverrides[provider.id].shippingTime;
    const description = [
        t('providerOffers.card.shippingTime', { shippingTime }),
    ];

    if (!provider) {
        throw new Error('No pharmacy for offer');
    }

    const price =
        typeof medicationPricing?.price === 'number'
            ? formatToLocalizedCurrency(medicationPricing.price)
            : medicationPricing?.price;

    let pricing: Pricing[] = [];
    if (medicationPricing) {
        pricing =
            medicationPricing?.type === 'selfPay'
                ? [
                      {
                          option: t('pricing.cashPrice'),
                          value: t('pricing.cashValue', {
                              price,
                          }),
                          details: t('pricing.newLowerPrice'),
                      },
                  ]
                : [
                      {
                          option: t('pricing.withInsurance'),
                          value: t('pricing.insuranceValue', {
                              price,
                          }),
                      },
                  ];
    }

    return (
        <PharmacyCard
            title={getProviderName(provider, pharmacyOverrides[provider.id])}
            description={description}
            onClick={() => onSelected(provider.id)}
            pricing={pricing}
        />
    );
};
